import React, { useContext, useEffect, useState } from "react";
import {
    IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonImg,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonModal,
  IonNote,
  IonRow,
} from "@ionic/react";

import { useLocation, useParams } from "react-router-dom";
import {
  airplane,
  add,
  addCircle,
  addSharp,
  heartSharp,
  mailOutline,
  mailSharp,
  paperPlaneOutline,
  paperPlaneSharp,
  trashOutline,
  trashSharp,
  warningOutline,
  warningSharp,
  addCircleOutline,
  addCircleSharp,
  checkmark,
  warning
} from "ionicons/icons";
import { DataContext } from "../context/DataContext";

type MenueDetails = {
  text: string;
  piktogramme: string[];
  bild: string;
};

const leereMenueDetails: MenueDetails = {
  text: "",
  piktogramme: [],
  bild: "",
};

const menueTitelCache: Record<string, MenueDetails> = {};

const leseStringAusWert = (wert: any): string => String(wert ?? "").trim();

const leseUrlListeAusWert = (wert: any): string[] => {
  if (Array.isArray(wert)) {
    return wert
      .map((eintrag: any) => {
        if (typeof eintrag === "string") {
          return eintrag.trim();
        }

        if (eintrag && typeof eintrag === "object") {
          return String(eintrag.url ?? eintrag.src ?? eintrag.bild ?? "").trim();
        }

        return "";
      })
      .filter((eintrag: string) => Boolean(eintrag));
  }

  const text = String(wert ?? "").trim();
  if (!text) {
    return [];
  }

  const htmlUrls = Array.from(text.matchAll(/https?:\/\/[^\s"'<>]+/g)).map((match) => match[0]);
  if (htmlUrls.length > 0) {
    return htmlUrls;
  }

  return text
    .split(/[;,|\n]+/)
    .map((eintrag) => eintrag.trim())
    .filter((eintrag) => /^https?:\/\//i.test(eintrag));
};

const leseMenueDetailsAusAntwort = (payload: any): MenueDetails => {
  if (!payload) {
    return leereMenueDetails;
  }

  const kandidat = Array.isArray(payload?.data)
    ? payload.data[0]
    : payload?.data && typeof payload.data === "object"
      ? payload.data
      : payload;

  if (!kandidat || typeof kandidat !== "object") {
    return leereMenueDetails;
  }

  const textDirekt = leseStringAusWert((kandidat as any).text ?? payload?.data?.text ?? payload?.text);
  const bildDirekt = leseStringAusWert((kandidat as any).bild ?? payload?.data?.bild ?? payload?.bild);
  const piktogramme = leseUrlListeAusWert((kandidat as any).piktogramme ?? payload?.data?.piktogramme ?? payload?.piktogramme);

  const moeglicheFelder = [
    "titel",
    "name",
    "menue",
    "menu",
    "bezeichnung",
    "campusmenu_titel",
    "campusmenu_name",
    "campusmenu_menue",
    "campusmenu_menu",
    "campusmenu_bezeichnung",
  ];

  let text = textDirekt;
  for (const feld of moeglicheFelder) {
    const wert = String((kandidat as any)[feld] ?? "").trim();
    if (!text && wert) {
      text = wert;
    }
  }

  return {
    text,
    piktogramme,
    bild: bildDirekt,
  };
};

const renderMenueTextMitSup = (text: string) => {
  const teile = String(text || "").split(/(\[sup\].*?\[\/sup\]|!u!)/g);

  return teile
    .filter((teil) => teil !== "")
    .map((teil, index) => {
      if (teil === "!u!") {
        return <br key={`break_${index}`} />;
      }

      const supMatch = teil.match(/^\[sup\](.*?)\[\/sup\]$/);

      if (supMatch) {
        return <sup key={`sup_${index}`}>{supMatch[1]}</sup>;
      }

      return <React.Fragment key={`text_${index}`}>{teil}</React.Fragment>;
    });
};

type Params = {
  dataAuswahl?: any;
  datum?: any;
  dataVerzehr?: any;
  schuelerPreisProzent?: any;
  schuelerId?: any;
  vorgemerkteKategorie?: number | null;
  vorgemerkteMenge?: number | null;
  serverKategorie?: number | null;
  serverMenge?: number | null;
  onMenueChange?: (payload: { kategorie: number; titel: string; preis: number; menge?: number } | null) => void;
  orderingBlocked?: boolean;
  orderingBlockedMessage?: string;
};

const Auswahl: React.FC<Params> = ({
  datum,
  schuelerPreisProzent,
  vorgemerkteKategorie,
  vorgemerkteMenge,
  serverKategorie,
  serverMenge,
  onMenueChange,
  orderingBlocked,
  orderingBlockedMessage,
}) => {
  const { appToken } = useContext(DataContext) as any;
  const datumObject = new Date(Date.parse(datum));
  const [ tagesmenueDetailsNachKategorie, setTagesmenueDetailsNachKategorie ] = useState<Record<number, MenueDetails>>({});
  const [ bildModalUrl, setBildModalUrl ] = useState("");

  const festeMenues = [
    { kategorie: 1, titel: "Vielfalt und Bunt" },
    { kategorie: 2, titel: "Vegi und Genuss" },
  ];
  const basisPreis = 5;
  const preisFaktor = Number(schuelerPreisProzent ?? 100) / 100;
  const menuePreis = Math.round(basisPreis * preisFaktor * 100) / 100;
  const menuePreisAnzeige = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(menuePreis);
  const istVorgemerktesStorno = vorgemerkteMenge === -1;
  const aktiveKategorie = istVorgemerktesStorno ? null : (vorgemerkteKategorie ?? (serverMenge === 0 ? null : serverKategorie));
  const aktiveMenge = (() => {
    if (istVorgemerktesStorno || aktiveKategorie === null || aktiveKategorie === undefined) {
      return null;
    }

    if (vorgemerkteKategorie !== null && vorgemerkteKategorie !== undefined) {
      const menge = Number(vorgemerkteMenge ?? 1);
      return Number.isNaN(menge) ? 1 : Math.abs(menge || 1);
    }

    const menge = Number(serverMenge ?? 1);
    return Number.isNaN(menge) || menge === 0 ? null : menge;
  })();
  const istKeinMenueAusgewaehlt = aktiveKategorie === null;

  const jetzt = new Date();
  const speiseplantag = new Date(
    datumObject.getFullYear(),
    datumObject.getMonth(),
    datumObject.getDate(),
  );
  const bestellDeadline = new Date(speiseplantag);
  bestellDeadline.setDate(bestellDeadline.getDate() - 2);
  const abbestellDeadline = new Date(speiseplantag);
  abbestellDeadline.setHours(8, 0, 0, 0);

  const bestellungGesperrt = jetzt >= bestellDeadline;
  const abbestellungGesperrt = jetzt >= abbestellDeadline;
  const isOrderingBlocked = Boolean(orderingBlocked);
  const blockedMessage = orderingBlockedMessage || "Bestellungen können erst in der 2. Hälfte der Sommerferien erstellt werden.";

  useEffect(() => {
    let istAktiv = true;

    if (!datum || !appToken) {
      setTagesmenueDetailsNachKategorie({});
      return () => {
        istAktiv = false;
      };
    }

    const parseJsonAntwort = async (response: Response) => {
      const raw = await response.text();
      if (!raw || !raw.trim()) {
        return null;
      }

      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const ladeMenueDetails = async (kategorie: number) => {
      const cacheKey = `${datum}_${kategorie}`;
      if (menueTitelCache[cacheKey] !== undefined) {
        return menueTitelCache[cacheKey];
      }

      try {
        const response = await fetch(
          "https://login.saint-online.de/funktionen/api2/campusmenu/getMenue.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              appToken,
              datum,
              kategorie,
            }),
          }
        );

        if (!response.ok) {
          return leereMenueDetails;
        }

        const data = await parseJsonAntwort(response);
        const details = leseMenueDetailsAusAntwort(data);
        menueTitelCache[cacheKey] = details;
        return details;
      } catch {
        return leereMenueDetails;
      }
    };

    Promise.all(
      festeMenues.map(async (menue) => {
        const details = await ladeMenueDetails(menue.kategorie);
        return [menue.kategorie, details] as const;
      })
    ).then((eintraege) => {
      if (!istAktiv) {
        return;
      }

      const next: Record<number, MenueDetails> = {};
      for (const [kategorie, details] of eintraege) {
        next[kategorie] = details;
      }
      setTagesmenueDetailsNachKategorie(next);
    });

    return () => {
      istAktiv = false;
    };
  }, [datum, appToken]);

  return (
    <IonCard>
      <IonItemDivider color="primary">
        <IonLabel style={{ fontFamily: '"Leckerli One", cursive' }}>
          {datumObject.toLocaleDateString("de-DE",{ dateStyle: "full" },)}
          {isOrderingBlocked ? (
            <span style={{ color: 'var(--ion-color-warning)' }}> - {blockedMessage}</span>
          ) : null}
          {istKeinMenueAusgewaehlt && !bestellungGesperrt && !isOrderingBlocked ? (
            <span style={{ color: 'var(--ion-color-warning)' }}> - Bitte wählen Sie ein Menü aus.</span>
          ) : null}
          {bestellungGesperrt && !abbestellungGesperrt && !isOrderingBlocked ? (
            <span style={{ color: 'var(--ion-color-medium)', fontSize: '0.8em' }}> - nur Abbestellung bis 8 Uhr möglich</span>
          ) : null}
          {abbestellungGesperrt && !isOrderingBlocked ? (
            <span style={{ color: 'var(--ion-color-medium)', fontSize: '0.8em' }}> - keine Änderung mehr möglich</span>
          ) : null}
        </IonLabel>
      </IonItemDivider>
      {isOrderingBlocked ? (
        <IonItem lines="none">
          <IonLabel color="warning">{blockedMessage}</IonLabel>
        </IonItem>
      ) : null}
      {!isOrderingBlocked ? festeMenues.map((menue) => {
        const istAusgewaehlt = aktiveKategorie === menue.kategorie;
        const istVorgemerkt = vorgemerkteKategorie === menue.kategorie && vorgemerkteMenge !== -1;
        const istServerAuswahlOhneVormerkung = serverKategorie === menue.kategorie && vorgemerkteKategorie === null;
        const istAbgegraut = !istKeinMenueAusgewaehlt && !istAusgewaehlt;
        const papierkorbSichtbar = (istVorgemerkt || istServerAuswahlOhneVormerkung) && !abbestellungGesperrt;

        return (
          <IonItem
            key={`menue_${menue.kategorie}`}
            style={{ "--background": istAbgegraut ? "#f3f3f3" : "transparent" } as any}
          >
            <IonIcon
              slot="start"
              icon={checkmark}
              color="success"
              style={{ visibility: istAusgewaehlt ? "visible" : "hidden" }}
            />
            <IonIcon
              slot="end"
              icon={trashOutline}
              color="warning"
              style={{ visibility: papierkorbSichtbar ? "visible" : "hidden" }}
              onClick={() => {
                if (!papierkorbSichtbar) {
                  return;
                }

                if (istVorgemerkt) {
                  onMenueChange?.(null);
                  return;
                }

                if (istServerAuswahlOhneVormerkung) {
                  onMenueChange?.({ kategorie: menue.kategorie, titel: menue.titel, preis: menuePreis, menge: -1 });
                }
              }}
            />

            <IonLabel
              onClick={() => {
                if (bestellungGesperrt) {
                  return;
                }

                onMenueChange?.({
                  kategorie: menue.kategorie,
                  titel: menue.titel,
                  preis: menuePreis,
                  menge: 1,
                });
              }}
              style={{ cursor: bestellungGesperrt ? 'default' : 'pointer' }}
            >
              {(() => {
                const menueDetails = tagesmenueDetailsNachKategorie[menue.kategorie] ?? leereMenueDetails;

                return (
                  <>
              <h3>
                Menü {menue.kategorie} - {(menue.titel === "Vegi und Genuss" || menue.titel === "Vielfalt und Bunt") ? <b>{istAusgewaehlt && aktiveMenge !== null ? `${aktiveMenge} x ` : ""}{menue.titel}</b> : <>{istAusgewaehlt && aktiveMenge !== null ? `${aktiveMenge} x ` : ""}{menue.titel}</>}
              </h3>
                  {(menueDetails.text || menueDetails.piktogramme.length > 0 || menueDetails.bild) ? (
                    <IonGrid style={{ padding: 0 }}>
                      <IonRow className="ion-align-items-start">
                        <IonCol size="2" style={{ paddingLeft: 0, paddingRight: "8px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {menueDetails.piktogramme.map((piktogrammUrl, index) => (
                              <IonImg
                                key={`piktogramm_${menue.kategorie}_${index}`}
                                src={piktogrammUrl}
                                alt={`Piktogramm ${index + 1}`}
                                style={{ width: "28px", height: "28px", objectFit: "contain" }}
                              />
                            ))}
                          </div>
                        </IonCol>
                        <IonCol size="8" style={{ paddingLeft: 0, paddingRight: "8px" }}>
                          {menueDetails.text ? <p>{renderMenueTextMitSup(menueDetails.text)}</p> : null}
                        </IonCol>
                        <IonCol size="2" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          {menueDetails.bild ? (
                            <IonImg
                              src={menueDetails.bild}
                              alt={`Menübild ${menue.kategorie}`}
                              style={{ width: "100%", borderRadius: "8px", objectFit: "cover", cursor: "pointer" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                setBildModalUrl(menueDetails.bild);
                              }}
                            />
                          ) : null}
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  ) : null}
              <p><b>{menuePreisAnzeige}</b></p>
                  </>
                );
              })()}
            </IonLabel>
          </IonItem>
        );
      }) : null}

      <IonModal isOpen={Boolean(bildModalUrl)} onDidDismiss={() => setBildModalUrl("")}>
        <IonContent className="ion-padding">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <IonButton onClick={() => setBildModalUrl("")}>Schließen</IonButton>
          </div>
          {bildModalUrl ? (
            <IonImg
              src={bildModalUrl}
              alt="Menübild groß"
              style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          ) : null}
        </IonContent>
      </IonModal>

    </IonCard>
  );
};

export default Auswahl;
